import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  TextField, Typography,
} from '@mui/material';
import api from '../services/api';
import { CopyField, fullLink } from './common';

const errorText = (err, fallback) => err?.response?.data?.error || fallback;

/**
 * Add a partner account, or edit one. A new account has no password: it gets a one-time link to
 * set its own, shown by PasswordHelp. onSaved(partner, passwordHelp?).
 */
export const PartnerDialog = ({ partner, onSaved, onClose }) => {
  const creating = !partner;
  const [form, setForm] = useState({
    first_name: partner?.first_name || '', last_name: partner?.last_name || '', email: partner?.email || '',
    company: partner?.company || '', username: partner?.username || '',
  });
  const [usernameTyped, setUsernameTyped] = useState(!creating);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const username = usernameTyped ? form.username : form.email.trim().toLowerCase();
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    setPending(true);
    setError(null);
    const body = { ...form, username };
    try {
      if (creating) {
        const { data } = await api.post('/auth/partners/', body);
        onSaved(data.partner, data.password_help);
      } else {
        const { data } = await api.patch(`/auth/partners/${partner.id}/`, body);
        onSaved(data);
      }
    } catch (err) {
      setError(errorText(err, "The account couldn't be saved."));
      setPending(false);
    }
  };

  return (
    <Dialog open onClose={pending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{creating ? 'Add a partner account' : `Edit ${partner.username}`}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField size="small" label="First name" value={form.first_name} onChange={set('first_name')} autoFocus />
        <TextField size="small" label="Last name" value={form.last_name} onChange={set('last_name')} />
        <TextField size="small" label="Email" type="email" value={form.email} onChange={set('email')} />
        <TextField size="small" label="Company" value={form.company} onChange={set('company')} />
        <TextField size="small" label="Username" required value={username}
          onChange={(e) => { setUsernameTyped(true); setForm((f) => ({ ...f, username: e.target.value })); }}
          helperText={creating ? 'What they sign in with. Their email address unless you change it.' : ' '} />
        {creating && (
          <Typography variant="body2" color="text.secondary">
            They&apos;ll get a one-time link to set their own password.
          </Typography>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={pending || !username.trim()} sx={{ textTransform: 'none' }}>
          {pending ? 'Saving…' : creating ? 'Add account' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Choose the views a partner sees in their list: every view, grouped by project and survey,
 * with a search. Saved as the partner's complete list. onSaved(partner).
 */
export const ChooseViewsDialog = ({ partner, onSaved, onClose }) => {
  const [monitors, setMonitors] = useState(null);
  const [selected, setSelected] = useState(() => new Set(partner.views.map((v) => v.id)));
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/quota-monitors/').then(({ data }) => setMonitors(data)).catch(() => setError("The views can't be loaded just now."));
  }, []);

  const needle = search.trim().toLowerCase();
  const shown = (monitors || []).filter((m) => !needle
    || [m.quota_view_name, m.survey_name, m.project_name].some((s) => (s || '').toLowerCase().includes(needle)));
  const groups = [];
  shown.forEach((m) => {
    const label = `${m.project_name} · ${m.survey_name}`;
    const group = groups.find((g) => g.label === label);
    if (group) group.views.push(m); else groups.push({ label, views: [m] });
  });
  groups.sort((a, b) => a.label.localeCompare(b.label));
  const toggle = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const save = async () => {
    setPending(true);
    setError(null);
    try {
      const { data } = await api.patch(`/auth/partners/${partner.id}/`, { view_ids: [...selected] });
      onSaved(data);
    } catch (err) {
      setError(errorText(err, "The views couldn't be saved."));
      setPending(false);
    }
  };

  return (
    <Dialog open onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Views {partner.first_name || partner.username} sees</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These appear in their list after signing in, and are the only ones they can open by number.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
          <TextField size="small" label="Search views, surveys or projects" value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setSelected(new Set())} disabled={!selected.size} sx={{ textTransform: 'none' }}>Clear all</Button>
        </Box>
        <Typography variant="body2" sx={{ mb: 1 }}>{selected.size} of {monitors ? monitors.length : '…'} views chosen</Typography>
        {!monitors && !error && <CircularProgress size={24} />}
        <Box sx={{ maxHeight: 380, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
          {groups.map((g) => (
            <Box key={g.label}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}
                sx={{ display: 'block', px: 1.5, pt: 1, position: 'sticky', top: 0, bgcolor: 'background.paper' }}>
                {g.label}
              </Typography>
              {g.views.map((m) => (
                <FormControlLabel key={m.id} sx={{ display: 'flex', mx: 0.5 }}
                  control={<Checkbox size="small" checked={selected.has(m.id)} onChange={() => toggle(m.id)} />}
                  label={`${m.quota_view_name} (${m.id})`} />
              ))}
            </Box>
          ))}
          {monitors && shown.length === 0 && <Typography color="text.secondary" sx={{ p: 2 }}>No views match.</Typography>}
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={pending || !monitors} sx={{ textTransform: 'none' }}>
          {pending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** The result of asking for a set-password link: emailed, or a link to copy and send. */
export const PasswordHelp = ({ help, partner, onClose }) => (
  <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Password link for {partner.username}</DialogTitle>
    <DialogContent>
      {help.sent ? (
        <Alert severity="success">A link to set their password was emailed to {help.email}.</Alert>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Ranger can&apos;t send email, so copy this link and send it to {partner.first_name || partner.username} yourself.
            It works once, for 3 days. It won&apos;t be shown again; you can always make a new one.
          </Typography>
          <CopyField label="Set-password link" value={fullLink(help.path)} />
        </>
      )}
    </DialogContent>
    <DialogActions>
      <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none' }}>Done</Button>
    </DialogActions>
  </Dialog>
);
